import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { ComboChartComponent } from '../../charts/combo-chart/combo-chart.component';
import { EducationOverviewService, OverviewResponse, EducationQueryRequest } from '../../../services/education-overview.service';
import { TemplateCommonService } from '../template-ai-common/services/template-common.service';
import { overview } from '../../sample-education';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-education-overview',
  standalone: true,
  imports: [
    CommonModule,
    MatAngularModule,
    NgxChartsModule,
    ComboChartComponent
  ],
  templateUrl: './education-overview.component.html',
  styleUrl: './education-overview.component.scss'
})
export class EducationOverviewComponent implements OnInit, OnDestroy {
  @Input() selectedEntities: any[] = [];

  overviewData: OverviewResponse | null = null;
  loading = false;
  error: string | null = null;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private educationOverviewService: EducationOverviewService,
    private templateCommonService: TemplateCommonService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    // Subscribe to analysis response from TemplateCommonService
    const analysisSub = this.templateCommonService.analysisResponse$.subscribe(response => {
        if (response) {
          // console.log('Education Overview received analysis response:', response);
          this.onReload();
        } else {
          // Use static data as fallback
          this.overviewData = overview;
          // console.log('Education Overview using static data as fallback');
        }
    });

    // Subscribe to loading state
    const loadingSub = this.templateCommonService.isLoading$.subscribe(loading => {
      this.loading = loading;
      if (loading) {
        this.overviewData = null;
      }
    });

    // Subscribe to error state
    const errorSub = this.templateCommonService.error$.subscribe(error => {
      this.error = error;
    });

    // Add subscriptions to array
    this.subscriptions.push(analysisSub, loadingSub, errorSub);

    // Use static data by default if no analysis response is available
    const currentResponse = this.templateCommonService.getCurrentAnalysisResponse();
    if (!currentResponse) {
      this.overviewData = overview;
      // console.log('Education Overview using static data initially');
    }
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  loadOverviewData(): void {
    this.loading = true;
    this.error = null;

    // Get current filter data from TemplateCommonService
    const currentFilterData = this.templateCommonService.getCurrentFilterData();

    if (!currentFilterData || !currentFilterData.selectedEntities || currentFilterData.selectedEntities.length === 0) {
      this.error = 'No universities selected for analysis';
      this.loading = false;
      this.overviewData = overview; // Use static data as fallback
      return;
    }

    // Create request with dynamic filter data
    const selectedUniversityNames = currentFilterData.selectedEntities.map(entity => entity.name);
    const isSingleUniversity = selectedUniversityNames.length === 1;

    // Create more specific query based on number of universities selected
    let queryText;
    if (isSingleUniversity) {
      queryText = `Analysis for ${selectedUniversityNames[0]} in ${currentFilterData.category} program in ${currentFilterData.country}`;
    } else {
      queryText = `Best universities for ${currentFilterData.category} in ${currentFilterData.country}`;
    }

    const request: EducationQueryRequest = {
      filters: {
        course: currentFilterData.category,
        country: currentFilterData.country === 'Global' ? undefined : currentFilterData.country,
        universities: selectedUniversityNames
      },
      query: queryText
    };

    // console.log('Education Overview - loadOverviewData with dynamic filters:', request);
    this.educationOverviewService.getOverviewAnalysis(request).subscribe({
        next: (data) => {
          this.overviewData = data;
          this.loading = false;
          // console.log('Education Overview data loaded:', data);
        },
      error: (err) => {
        this.error = 'Failed to load overview data';
        this.loading = false;
        console.error('Education Overview data error:', err);

        // Use static data as fallback
        this.overviewData = overview;
        this.loading = false;
      }
    });
  }


  getScoreColor(score: number): string {
    if (score >= 90) return '#4caf50'; // Green
    if (score >= 80) return '#ff9800'; // Orange
    if (score >= 70) return '#ff5722'; // Red
    return '#9e9e9e'; // Grey
  }

  retryLoad(): void {
    this.loadOverviewData();
  }


  // Method to trigger reload from parent component
  onReload(): void {
    // console.log('Education Overview - Reload triggered');
    // Use the dynamic loadOverviewData method instead of template common service
    this.loadOverviewData();
  }
}
